import React from 'react';
import { connect, ConnectedProps } from '@cerebral/react';
import { state, sequences } from 'app/pwa/app.cerebral';

import {
  getIndicatorConfig,
  getIndicator,
  datetimeIsStartOfDay,
} from 'utils/common';
import { DATE_FORMAT, ICON } from 'resources/constants';

import StreakCircle from 'challenge/common/StreakCircle';
import {
  Wrapper,
  Header,
  StatsContainer,
  ProgressWrapper,
  ListContainer,
  DayProgressBar,
  StreakWrapper,
  TextWrapper,
  Title,
  Text,
} from './elements';

import { useText, useFormatIndicator, useFormatDate } from 'common/hooks';

const deps = {
  insights: state.challenge.visualisations.computedPersonalInsights,
  startOfDays: state.challenge.campaign.challenge.startOfDays,
  dailyUserStats: state.challenge.stats.daily.user,
  indicator: state.challenge.campaign.indicator,
  streakModalClicked: sequences`challenge.info.streakModalClicked`,
};

export function PersonalInsights({
  insights,
  startOfDays = [],
  dailyUserStats,
  indicator,
  streakModalClicked,
}: typeof deps & ConnectedProps): React.ReactElement {
  const t = useText();
  const formatIndicator = useFormatIndicator();
  const formatDate = useFormatDate();

  const { icon, color } = getIndicatorConfig(indicator);
  const { highestScore, streakInfo, currentStreak } = insights;

  return (
    <Wrapper>
      <Header
        title={t(
          `challenge.ChaPaD24B.PersonalInsights.your_daily_savings.${indicator}`
        )}
      />
      {highestScore > 0 && startOfDays && (
        <StatsContainer>
          <StreakWrapper onClick={() => streakModalClicked()}>
            <StreakCircle
              size={'large'}
              icon={ICON.flame}
              iconColor={'#FF7043'}
              data={streakInfo.data}
            />
            <TextWrapper>
              <Title>
                {t(
                  `challenge.ChaPaD24B.PersonalInsights.streaks.title.${streakInfo.title}`,
                  {
                    streak: currentStreak.length,
                    totalLogged: streakInfo.daysLogged,
                    totalDays: startOfDays.length,
                  }
                )}
              </Title>
              <Text>
                {t(
                  `challenge.ChaPaD24B.PersonalInsights.streaks.text.${streakInfo.text}`,
                  {
                    totalDays: startOfDays.length,
                  }
                )}
              </Text>
            </TextWrapper>
          </StreakWrapper>
          <ProgressWrapper>
            <ListContainer>
              {(startOfDays || []).map(datetime => {
                const iconColor = currentStreak.includes(datetime)
                  ? '#FF7043'
                  : '#CFD8DC';

                return (
                  <DayProgressBar
                    key={`day-progress-day-${datetime}`}
                    valueIcon={icon}
                    progressColor={color}
                    type={'icon'}
                    icon={ICON.flame}
                    iconColor={
                      dailyUserStats[datetime] ? iconColor : 'transparent'
                    }
                    labelText={formatDate(
                      datetime,
                      datetimeIsStartOfDay(datetime)
                        ? DATE_FORMAT.DAY_MONTH_SHORT
                        : DATE_FORMAT.DAY_MONTH_TIME_SHORT
                    )}
                    percent={
                      highestScore === 0
                        ? highestScore
                        : ((dailyUserStats?.[datetime]?.[indicator] ?? 0) /
                            highestScore) *
                          100
                    }
                    value={formatIndicator(
                      getIndicator(dailyUserStats[datetime], indicator),
                      indicator
                    )}
                  />
                );
              })}
            </ListContainer>
          </ProgressWrapper>
        </StatsContainer>
      )}
    </Wrapper>
  );
}

export default connect(deps, PersonalInsights);
